/**
 * @file RepReviewDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Approve or hold a rep's prices from Rep Pricing's Priced by rep
 *              tab — and, if the person chooses, tell the rep (Cam, 28 Oct
 *              2026, asked whether the rep is told when the office approves,
 *              holds or asks again: "Allow user to decide").
 *
 *              Lists what will happen to each line (customer, product, order,
 *              the rep's price) and offers "Email <rep> about this" — unticked
 *              by default, so nothing is sent unless chosen. Ticked, the email
 *              is shown as it will go (POST /api/pricing-costing/
 *              rep-review-email/preview): To, Cc, Subject and Body, all
 *              editable. Approve / Hold sends POST /rep-replies/review with
 *              the ticked emails; the server writes the review first and then
 *              sends each email, only for the lines it actually did
 *              (sendTracked + Activity). "Ask again" is not here — it is a
 *              send, through the Send to reps window.
 *
 *              Props: { action: 'approve'|'hold', requests (the Priced by rep
 *              requests), onClose, onDone(body) }.
 */

import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import SenderSelect from './SenderSelect';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { money, ukDate } from '../utils/pricingCosting';
import { lineIdsOf } from '../utils/repPricingView';
import './RepPricingView.css';

const WORDS = {
  approve: { title: 'Approve the rep\'s prices', verb: 'Approve', icon: 'verified', did: 'approved — the sale price is confirmed on the order' },
  hold: { title: 'Hold the rep\'s prices for review', verb: 'Hold', icon: 'pause_circle', did: 'held for review — nothing on the order changes' }
};

function RepReviewDialog({ action, requests, onClose, onDone }) {
  const words = WORDS[action] || WORDS.approve;
  const lineIds = useMemo(() => lineIdsOf(requests), [requests]);
  const reps = useMemo(() => {
    const m = new Map();
    for (const q of requests || []) {
      const k = q.rep_id || 0;
      if (!m.has(k)) m.set(k, { rep_id: q.rep_id || null, rep_name: q.rep_name || 'No rep', lines: 0 });
      m.get(k).lines += (q.line_ids || []).length;
    }
    return [...m.values()];
  }, [requests]);
  // Emails per rep: { ticked, to, cc, subject, body, block } — filled from the preview.
  const [emails, setEmails] = useState({});
  const [preview, setPreview] = useState({ loading: false, error: null, loaded: false });
  const [sendAs, setSendAs] = useState('me');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const anyTicked = Object.values(emails).some(e => e.ticked);
  const loadPreview = async () => {
    if (preview.loaded || preview.loading) return;
    setPreview({ loading: true, error: null, loaded: false });
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-review-email/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lineIds, action })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'The email could not be prepared');
      setEmails(prev => {
        const next = { ...prev };
        for (const e of body.emails || []) {
          const k = e.rep_id || 0;
          next[k] = { ...e, cc: '', ticked: !!(prev[k] && prev[k].ticked) && !e.block };
        }
        return next;
      });
      setPreview({ loading: false, error: null, loaded: true });
    } catch (err) {
      setPreview({ loading: false, error: err.message, loaded: false });
    }
  };

  const tick = (repKey, on) => {
    setEmails(prev => ({ ...prev, [repKey]: { ...(prev[repKey] || {}), ticked: on } }));
    if (on) loadPreview();
  };
  const edit = (repKey, patch) => setEmails(prev => ({ ...prev, [repKey]: { ...prev[repKey], ...patch } }));

  const emailProblem = Object.values(emails).filter(e => e.ticked && e.rep_id)
    .map(e => (!String(e.to || '').trim() ? `Who should the email to ${e.rep_name} go to?` : (!String(e.subject || '').trim() || !String(e.body || '').trim()) ? 'The email needs a subject and words' : null))
    .find(Boolean) || null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const out = Object.values(emails).filter(e => e.ticked && e.rep_id && !e.block)
        .map(e => ({ repId: e.rep_id, to: e.to, cc: e.cc, subject: e.subject, body: e.body, sendAs }));
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-replies/review`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lineIds, action, ...(out.length ? { emails: out } : {}) })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Nothing was changed');
      onDone(body);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const footer = (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose} disabled={busy}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout" onClick={submit} disabled={busy || !lineIds.length || (anyTicked && (!!emailProblem || preview.loading))}
          title={emailProblem || ''}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">{words.icon}</span></div>
          <span className="btn-label">{busy ? 'Saving…' : `${words.verb} ${lineIds.length} line${lineIds.length === 1 ? '' : 's'}${anyTicked ? ' and email' : ''}`}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen onClose={busy ? undefined : onClose} title={words.title} size="lg" className="rp-review" footer={footer}>
      <ReviewBody
        action={action} words={words} requests={requests} reps={reps} emails={emails} preview={preview}
        tick={tick} edit={edit} sendAs={sendAs} setSendAs={setSendAs} error={error} busy={busy} emailProblem={emailProblem}
      />
    </Modal>
  );
}

function ReviewBody({ words, requests, reps, emails, preview, tick, edit, sendAs, setSendAs, error, busy, emailProblem }) {
  useSlideOutButton([busy, Object.values(emails).some(e => e.ticked)]);
  const anyTicked = Object.values(emails).some(e => e.ticked);
  return (
    <div className="rp-review-body">
      {error && <p className="rp-note err"><span className="material-symbols-rounded">error</span>{error}</p>}
      <p>These rep prices will be {words.did}:</p>
      <ul className="rp-review-lines">
        {(requests || []).map(q => (
          <li key={q.key}>
            <b>{q.customer?.account_no ? `${q.customer.account_no} ` : ''}{q.customer?.name}</b> · {q.product?.name}
            {q.product?.code ? <span className="rp-muted"> {q.product.code}</span> : null}
            <div className="rp-muted">
              {(q.orders || []).map(o => `${o.order_number}${o.delivery_date ? ` (${ukDate(o.delivery_date)})` : ''}${o.rep_price !== null && o.rep_price !== undefined ? ` at ${money(o.rep_price)}${q.per || ''}` : ''}`).join(' · ')}
              {' — '}{q.rep_name || 'no rep'}{q.held ? ' · held' : ''}
            </div>
          </li>
        ))}
      </ul>
      <div className="rp-review-email">
        <h4>Tell the rep?</h4>
        <p className="rp-muted">Your choice each time — nothing is emailed unless you tick it.</p>
        {reps.map(r => {
          const k = r.rep_id || 0;
          const e = emails[k] || {};
          const block = !r.rep_id ? 'No rep on the account — nobody to tell' : (e.block || null);
          return (
            <div key={k} className="rp-review-rep">
              <TickCheckbox id={`rp-email-${k}`} checked={!!e.ticked && !block} disabled={!!block || busy}
                onChange={(ev) => tick(k, ev.target.checked)}
                label={`Email ${r.rep_name} about this (${r.lines} line${r.lines === 1 ? '' : 's'})`} />
              {block && <span className="rp-muted"> — {block}</span>}
              {e.ticked && !block && (
                preview.loading && !e.subject ? <LoadingSpinner /> : (
                  <div className="rp-review-fields">
                    <label>To<Input value={e.to || ''} onChange={(ev) => edit(k, { to: ev.target.value })} /></label>
                    <label>Cc<Input value={e.cc || ''} placeholder="Optional" onChange={(ev) => edit(k, { cc: ev.target.value })} /></label>
                    <label>Subject<Input value={e.subject || ''} onChange={(ev) => edit(k, { subject: ev.target.value })} /></label>
                    <label>Email<Textarea value={e.body || ''} rows={10} onChange={(ev) => edit(k, { body: ev.target.value })} /></label>
                  </div>
                )
              )}
            </div>
          );
        })}
        {preview.error && <p className="rp-note err"><span className="material-symbols-rounded">error</span>{preview.error}</p>}
        {anyTicked && (
          <label className="rp-review-from">Send from<SenderSelect value={sendAs} onChange={setSendAs} /></label>
        )}
        {anyTicked && emailProblem && <p className="rp-note warn"><span className="material-symbols-rounded">warning</span>{emailProblem}</p>}
      </div>
    </div>
  );
}

export default RepReviewDialog;
